<template>
  <Teleport to="body">
    <Transition name="popover-fade">
      <div v-if="isOpen" class="funnel-popover-backdrop" @click="handleClose">
        <div
            class="funnel-popover"
            :style="popoverStyle"
            @click.stop
        >
          <div class="funnel-popover-header">
            <h4>{{ $t('funnelPopover.title') }}</h4>
            <button class="close-btn" @click="handleClose">
              <img class="cross" src="../../assets/images/cross.svg"/>
            </button>
          </div>

          <div class="funnel-popover-body">
            <div v-if="!moduleProgress || moduleProgress.length === 0" class="funnel-empty">
              <span>{{ $t('funnelPopover.noModules') }}</span>
            </div>

            <div v-else class="funnel-items">
              <div
                  v-for="(module, index) in moduleProgress"
                  :key="index"
                  class="funnel-item"
              >
                <div class="funnel-item-wrapper">
                  <div
                      class="funnel-bar"
                      :style="{
                      width: `${calculateBarWidth(module.progress)}%`,
                      backgroundColor: getModuleColor(index)
                    }"
                  >
                    <span class="funnel-module-name">{{module.name}}</span>
                  </div>

                  <div class="funnel-progress-info">
                    <svg class="progress-circle" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="7" cy="7" r="6" stroke="#D0D5DD" stroke-width="1.5" fill="none"/>
                    </svg>
                    <span class="funnel-progress-text">%{{ module.progress }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
    default: false
  },
  program: {
    type: Object,
    default: null
  },
  triggerElement: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const popoverStyle = ref({})

const moduleProgress = computed(() => {
  return props.program?.moduleProgress || []
})

const handleClose = () => {
  emit('close')
}

const handleScroll = () => {
  if (props.isOpen) {
    handleClose()
  }
}

const calculatePopoverPosition = () => {
  if (!props.triggerElement) return

  const rect = props.triggerElement.getBoundingClientRect()
  const popoverWidth = 240
  const offsetTop = 10

  let top = rect.bottom + offsetTop
  let left = rect.left + (rect.width / 2) - (popoverWidth / 2)

  const viewportWidth = window.innerWidth

  if (left + popoverWidth > viewportWidth - 8) {
    left = viewportWidth - popoverWidth - 8
  }

  if (left < 8) {
    left = 8
  }

  console.log('Progress Bar Rect:', {
    bottom: rect.bottom,
    left: rect.left,
    width: rect.width
  })
  console.log('Modal Position:', { top, left })

  popoverStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    position: 'fixed'
  }
}

const calculateBarWidth = (progress) => {
  const numProgress = Number(progress) || 0
  const minWidth = 40
  const maxWidth = 100
  return minWidth + (numProgress / 100) * (maxWidth - minWidth)
}

const getModuleColor = (index) => {
  const colors = [
    '#3B4DBF', '#5B7FFF', '#8B5CF6', '#F97316',
    '#22C55E', '#EAB308', '#EC4899', '#14B8A6',
  ]
  return colors[index % colors.length]
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      calculatePopoverPosition()
    }, 10)

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEsc)
    document.addEventListener('scroll', handleScroll, true)
    window.addEventListener('scroll', handleScroll, true)

    const cleanup = () => {
      document.removeEventListener('keydown', handleEsc)
      document.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('scroll', handleScroll, true)
    }

    return cleanup
  }
})

onMounted(() => {
  if (props.isOpen) {
    calculatePopoverPosition()
    document.addEventListener('scroll', handleScroll, true)
    window.addEventListener('scroll', handleScroll, true)
  }
})

onUnmounted(() => {
  document.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('scroll', handleScroll, true)
})
</script>
<style scoped>
.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.popover-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.funnel-popover-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  background: transparent;
}

.funnel-popover {
  position: fixed; /* CRITICAL */
  width: 240px;
  max-height: 350px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.funnel-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #E4E7EC;
  background: white;
}

.funnel-popover-header h4 {
  font-size: 12px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
  font-family: 'euclid_semi_bold', sans-serif;
  line-height: 1.3;
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
  border: none;
  cursor: pointer;
  background-color: #f2f4f7;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #F2F4F7;
}

.funnel-popover-body {
  padding: 12px;
  overflow-y: auto;
  max-height: 298px;
  background: #FAFBFC;
}

.funnel-popover-body::-webkit-scrollbar {
  width: 5px;
}

.funnel-popover-body::-webkit-scrollbar-track {
  background: #F0F1F3;
  border-radius: 3px;
}

.funnel-popover-body::-webkit-scrollbar-thumb {
  background: #D0D5DD;
  border-radius: 3px;
}

.funnel-popover-body::-webkit-scrollbar-thumb:hover {
  background: #98A2B3;
}

.funnel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
  text-align: center;
}

.funnel-empty span {
  color: #667085;
  font-size: 11px;
  font-family: 'euclid_regular', sans-serif;
}

.funnel-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.funnel-item {
  width: 100%;
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.funnel-item-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.funnel-bar {
  min-height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.funnel-bar:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
}

.funnel-module-name {
  font-weight: 600;
  font-size: 11px;
  color: white;
  font-family: 'euclid_semi_bold', sans-serif;
  text-align: center;
  white-space: nowrap;
}

.funnel-progress-info {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  margin-left: auto;
}

.progress-circle {
  flex-shrink: 0;
}

.funnel-progress-text {
  font-size: 11px;
  font-weight: 600;
  color: #1F2937;
  font-family: 'euclid_semi_bold', sans-serif;
  white-space: nowrap;
  min-width: 32px;
}

@media (max-width: 768px) {
  .funnel-popover {
    width: 220px;
    max-height: 320px;
  }

  .funnel-popover-body {
    max-height: 268px;
    padding: 10px;
  }

  .funnel-popover-header {
    padding: 10px 12px;
  }

  .funnel-popover-header h4 {
    font-size: 11px;
  }

  .funnel-items {
    gap: 8px;
  }

  .funnel-item-wrapper {
    gap: 6px;
  }

  .funnel-bar {
    min-height: 28px;
    padding: 5px 8px;
    border-radius: 5px;
  }

  .funnel-module-name {
    font-size: 10px;
  }

  .funnel-progress-text {
    font-size: 10px;
    min-width: 28px;
  }

  .progress-circle {
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 480px) {
  .funnel-popover {
    width: 200px;
  }

  .funnel-popover-body {
    padding: 10px;
  }

  .funnel-items {
    gap: 8px;
  }

  .funnel-bar {
    min-height: 28px;
  }
}
</style>
